import type { ApiInputToken } from '@morpho-org/dapp-core'

import { UNKNOWN_SYMBOL } from '@morpho-org/ui-v6/utils'
import { skipToken } from '@tanstack/react-query'
import { getAddress, isAddress } from 'viem'

import type {
  UserBorrowPositions,
  UserPositionAssetFacet
} from '@/lib/procedures/userPositions.contract'

import { orpc } from '@/lib/orpc/client'

export type UserBorrowPosition = UserBorrowPositions['items'][number]
export type UserBorrowPositionFacets = UserBorrowPositions['facets']

/**
 * The one `user.borrowPositions` query the Portfolio borrow table, the borrow hero, the lend
 * table's market ids and the nav badge share, so they read one cache entry. The table sorts its
 * rows itself, so no order is sent.
 */
export function userBorrowPositionsQueryOptions(userAddress: string | undefined) {
  return orpc.user.borrowPositions.queryOptions({
    input:
      userAddress && isAddress(userAddress, { strict: false })
        ? { userAddress: getAddress(userAddress) }
        : skipToken,
    staleTime: 60 * 1000,
    select: withFacetTokenNames
  })
}

const tokenKey = (token: { chainId: number; address: string }) =>
  `${token.chainId}:${token.address.toLowerCase()}`

/** Labels a facet token with no symbol by its name from the rows, as `/consumer` did. */
export function withFacetTokenNames(data: UserBorrowPositions): UserBorrowPositions {
  const names = new Map<string, string>()
  for (const { market } of data.items) {
    for (const token of [market.loanAsset, market.collateralAsset]) {
      if (token?.name != null) names.set(tokenKey(token), token.name)
    }
  }

  const withNames = (facets: UserPositionAssetFacet[]) =>
    facets.map(facet =>
      facet.metadata.symbol == null
        ? {
            ...facet,
            metadata: { ...facet.metadata, symbol: names.get(tokenKey(facet.metadata)) ?? null }
          }
        : facet
    )

  return {
    ...data,
    facets: {
      ...data.facets,
      loanAssets: withNames(data.facets.loanAssets),
      collateralAssets: withNames(data.facets.collateralAssets)
    }
  }
}

type UserBorrowPositionToken = UserBorrowPosition['market']['borrowRewards'][number]['asset']

/** A `user.borrowPositions` token (`logoUri`, nullable `symbol`) → `ApiInputToken`. */
export const toBorrowPositionInputToken = (
  token: UserBorrowPositionToken & { priceUsd?: number | null }
): ApiInputToken => ({
  address: token.address,
  symbol: token.symbol ?? UNKNOWN_SYMBOL,
  name: token.name ?? token.symbol ?? UNKNOWN_SYMBOL,
  decimals: token.decimals,
  logoURI: token.logoUri,
  chain: { id: token.chainId },
  priceUsd: token.priceUsd ?? null
})
